import type { LoginStatusSyncResult } from '@/app/api/auth/login-status/route'
import { enableLoginStatusCheck } from '@/lib/configs'
import type { AuthSnapshot } from '@/server/lib/auth-snapshot'

export const LOGIN_STATUS_SYNC_ENDPOINT = '/api/auth/login-status'

/**
 * Gates on the access-cookie presence rather than on `isSignedIn`.
 *
 * `isSignedIn` is false whenever the profile lookup failed — which is exactly the stale-token case
 * this pass exists to repair. Gating on it would skip the check at the only moment it matters. The
 * cookie is the real question: the browser presents one, so Reticulum has to say what it is worth.
 */
export const shouldSyncLoginStatus = ({
  isExternalIframe,
  hasHtcAccessToken,
}: AuthSnapshot): boolean =>
  enableLoginStatusCheck() && !isExternalIframe && hasHtcAccessToken

const isSyncResult = (value: unknown): value is LoginStatusSyncResult =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { kind?: unknown }).kind === 'string'

/**
 * Asks the route handler to validate the session. The handler owns the `Set-Cookie` write, so any
 * rotation Reticulum performs lands on this response.
 *
 * Returns `null` when the outcome must not change anything — a transport failure, a bad response,
 * or an explicit `unavailable`. CONNECT-5667 established that a transient dependency failure must
 * never sign anyone out.
 */
export const fetchLoginStatusSync = async (): Promise<LoginStatusSyncResult | null> => {
  let result: unknown

  try {
    const response = await fetch(LOGIN_STATUS_SYNC_ENDPOINT, { cache: 'no-store' })

    if (!response.ok) return null

    result = await response.json()
  } catch {
    return null
  }

  if (!isSyncResult(result) || result.kind === 'unavailable') return null

  return result
}
